import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { motion } from "framer-motion";
import DashboardLayout from "../../components/dashboard/DashboardLayout";
import StatCard from "../../components/dashboard/StatCard";
import Button from "../../components/ui/Button";
import creditService from "../../utils/creditService";
import stripeService from "../../utils/stripeService";
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";
import Icon from "../../components/AppIcon";
import { supabase } from "../../utils/supabase";

function CreditsPage() {
  const [stats, setStats] = useState({
    totalLeads: 0,
    totalMeetings: 0,
    avgReplyRate: 0,
  });
  const [creditBalance, setCreditBalance] = useState(0);
  const [creditStats, setCreditStats] = useState({
    totalPurchased: 0,
    totalUsed: 0,
    totalRefunded: 0,
    transactionCount: 0,
  });
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { user } = useAuth();

  // Chart data for credit transactions over time
  const [creditChartData, setCreditChartData] = useState([]);

  // Track subscriptions for cleanup
  const subscriptionsRef = useRef([]);

  useEffect(() => {
    loadDashboardData();
    setupRealtimeSubscriptions();

    // Cleanup subscriptions on unmount
    return () => {
      subscriptionsRef.current.forEach(subscription => {
        if (subscription && typeof subscription.unsubscribe === 'function') {
          subscription.unsubscribe();
        }
      });
    };
  }, [user?.id]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Load campaign stats - get leads generated, meetings booked, and replies
      const [leadsResult, meetingsResult, repliesResult] = await Promise.all([
        // Get leads generated count
        supabase
          .from("apollo_leads")
          .select("*", { count: "exact", head: true }),
        // Get meetings booked count
        supabase.from("meetings").select("*", { count: "exact", head: true }),
        // Get replies count
        supabase
          .from("apollo_leads")
          .select("*", { count: "exact", head: true })
          .eq("replied", true),
      ]);

      const newStats = {
        totalLeads: leadsResult?.count || 0,
        totalMeetings: meetingsResult?.count || 0,
        totalReplies: repliesResult?.count || 0,
      };
      setStats(newStats);

      // Load credit data if user is available
      if (user?.id) {
        await refreshCreditData();
      }
    } catch (err) {
      setError("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Setup real-time subscriptions for credit transactions
   * Automatically updates credit balance and stats when transactions occur
   */
  const setupRealtimeSubscriptions = async () => {
    if (!user?.id) return;

    try {
      // Subscribe to credit transactions changes
      const creditSubscription = supabase
        .channel(`credit_transactions_${user.id}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'credit_transactions',
            filter: `user_id=eq.${user.id}`
          },
          async (payload) => {
            // Refresh credit data when a transaction occurs
            await refreshCreditData();
          }
        )
        .subscribe();

      subscriptionsRef.current.push(creditSubscription);
    } catch (err) {
      console.error('Error setting up real-time subscriptions:', err);
    }
  };

  /**
   * Refresh credit data (balance, stats, transactions, and chart)
   * Called when real-time events occur or after payment success
   */
  const refreshCreditData = async () => {
    try {
      if (!user?.id) return;

      // Get updated credit balance
      let currentBalance = creditBalance; // Default to state value
      const creditResult = await creditService.getCreditBalance(user.id);
      if (creditResult?.success) {
        currentBalance = creditResult.data.credits; // Use the fresh value from API
        setCreditBalance(currentBalance);
      }

      // Get updated credit stats
      const creditStatsResult = await creditService.getCreditStats(
        user.id,
        "30_days"
      );
      if (creditStatsResult?.success) {
        setCreditStats(creditStatsResult.data);
      }

      // Get updated transaction history
      const transactionsResult = await creditService.getCreditTransactions(
        user.id,
        30
      );
      if (transactionsResult?.success) {
        setTransactionHistory(transactionsResult.data);

        // Transform transaction data for updated chart using the CURRENT balance
        const chartData = transformTransactionsToChartData(
          transactionsResult.data,
          currentBalance // Use the fresh balance, not state
        );
        setCreditChartData(chartData);
      }
    } catch (err) {
      console.error('Error refreshing credit data:', err);
    }
  };

  // Transform credit transactions into chart-friendly format
  const transformTransactionsToChartData = (transactions, currentBalance) => {
    if (!transactions?.length) {
      return [];
    }

    // Use the passed currentBalance parameter (which is the actual current balance)
    const actualCurrentBalance = currentBalance !== undefined ? currentBalance : creditBalance;

    // Group transactions by date
    const groupedByDate = {};
    const last30Days = [];

    // Create array of last 30 days
    for (let i = 29; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateKey = date.toISOString().split("T")[0];
      last30Days.push(dateKey);
      groupedByDate[dateKey] = {
        date: dateKey,
        purchase: 0,
        usage: 0,
        balance: 0,
        day: date.getDate(),
      };
    }

    // Sort transactions by date ascending
    const sortedTransactions = [...transactions].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );

    // Calculate starting balance by working backwards from current balance
    let startingBalance = actualCurrentBalance;
    sortedTransactions.forEach((transaction) => {
      if (transaction.transaction_type === "purchase") {
        startingBalance -= transaction.credits_amount;
      } else if (transaction.transaction_type === "deduction") {
        startingBalance += transaction.credits_amount;
      } else if (transaction.transaction_type === "refund") {
        startingBalance -= transaction.credits_amount;
      }
    });

    // Now process transactions forward to build chart data
    let runningBalance = Math.max(0, startingBalance);

    sortedTransactions.forEach((transaction) => {
      const transactionDate = new Date(transaction.created_at)
        .toISOString()
        .split("T")[0];

      if (groupedByDate[transactionDate]) {
        if (transaction.transaction_type === "purchase") {
          groupedByDate[transactionDate].purchase += transaction.credits_amount;
          runningBalance += transaction.credits_amount;
        } else if (transaction.transaction_type === "deduction") {
          groupedByDate[transactionDate].usage += transaction.credits_amount;
          runningBalance -= transaction.credits_amount;
        } else if (transaction.transaction_type === "refund") {
          groupedByDate[transactionDate].purchase += transaction.credits_amount;
          runningBalance += transaction.credits_amount;
        }
        groupedByDate[transactionDate].balance = Math.max(0, runningBalance);
      }
    });

    // Convert to array format for chart and ensure each day has a balance
    const chartData = last30Days.map((dateKey) => groupedByDate[dateKey]);

    // Fill in missing balances (carry forward last known balance)
    let lastBalance = Math.max(0, startingBalance);
    for (let i = 0; i < chartData.length; i++) {
      if (chartData[i].balance === 0 && chartData[i].purchase === 0 && chartData[i].usage === 0) {
        chartData[i].balance = lastBalance;
      } else {
        lastBalance = chartData[i].balance;
      }
    }

    return chartData;
  };

  const handlePurchaseCredits = async (creditPackage) => {
    setSelectedCreditPackage(creditPackage);
    setShowPaymentForm(true);
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    try {
      // Wait a brief moment for webhook to process
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Refresh all dashboard data after successful payment
      await loadDashboardData();
      await refreshCreditData();

      setShowPaymentForm(false);
      setShowCreditModal(false);
      setSelectedCreditPackage(null);

      // Clear any existing errors
      setError("");

      // Show success message
      console.log('Credits purchased successfully! ✨');
    } catch (err) {
      console.error("Error refreshing data after payment:", err);
      setError('Payment completed, but there was an error loading the updated balance. Please refresh the page.');
    }
  };

  const handlePaymentError = (error) => {
    setError(`Payment failed: ${error}`);
    setShowPaymentForm(false);
  };

  const handlePaymentCancel = () => {
    setShowPaymentForm(false);
    setSelectedCreditPackage(null);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="glassmorphism rounded-lg p-3 border border-border">
          <p className="text-sm font-body-medium text-foreground mb-2">
            Day {label}
          </p>
          {data.purchase > 0 && (
            <p className="text-xs text-success">
              Purchased: +{data.purchase} credits
            </p>
          )}
          {data.usage > 0 && (
            <p className="text-xs text-warning">Used: -{data.usage} credits</p>
          )}
          <p className="text-xs text-primary font-semibold">
            Balance: {data.balance} credits
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <DashboardLayout title="Dashboard" currentPath="/credits">
        <div className="flex items-center justify-center h-64">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground font-body-medium">
              Loading Credits...
            </p>
          </motion.div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Dashboard" currentPath="/credits">
      <div className="space-y-8">
        {/* Error Notice */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glassmorphism rounded-lg p-4 border border-error/20 bg-error/10"
          >
            <div className="text-sm text-error font-body-medium">{error}</div>
          </motion.div>
        )}



        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <StatCard
            title="Credit Balance"
            value={creditBalance}
            icon="Coins"
            color="warning"
            change={
              creditStats.totalPurchased > creditStats.totalUsed
                ? Math.round(
                  ((creditStats.totalPurchased - creditStats.totalUsed) /
                    Math.max(creditStats.totalUsed, 1)) *
                  100
                )
                : -Math.round(
                  ((creditStats.totalUsed - creditStats.totalPurchased) /
                    Math.max(creditStats.totalPurchased, 1)) *
                  100
                )
            }
          />
        </div>

        {/* Credit Activity Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="glassmorphism rounded-xl p-6"
        >
          <h3 className="text-lg font-headline-bold text-foreground mb-6">
            Credit Activity - Last 30 Days
          </h3>
          <div className="h-80">
            {creditChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={creditChartData}>
                  <defs>
                    <linearGradient
                      id="balanceGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="var(--color-primary)"
                        stopOpacity={0.3}
                      />
                      <stop
                        offset="95%"
                        stopColor="var(--color-primary)"
                        stopOpacity={0.1}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--color-border)"
                  />
                  <XAxis
                    dataKey="day"
                    tick={{
                      fill: "var(--color-muted-foreground)",
                      fontSize: 12,
                    }}
                    stroke="var(--color-border)"
                  />
                  <YAxis
                    tick={{
                      fill: "var(--color-muted-foreground)",
                      fontSize: 12,
                    }}
                    stroke="var(--color-border)"
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="balance"
                    stroke="var(--color-primary)"
                    fillOpacity={1}
                    fill="url(#balanceGradient)"
                    strokeWidth={2}
                  />
                  <Bar
                    dataKey="purchase"
                    fill="var(--color-success)"
                    opacity={0.7}
                    radius={[2, 2, 0, 0]}
                  />
                  <Bar
                    dataKey="usage"
                    fill="var(--color-warning)"
                    opacity={0.7}
                    radius={[2, 2, 0, 0]}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted/20 flex items-center justify-center">
                    <Icon
                      name="BarChart3"
                      size={32}
                      color="var(--color-muted-foreground)"
                    />
                  </div>
                  <p className="text-muted-foreground font-body-medium">
                    No credit activity yet
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Purchase credits or create campaigns to see your activity
                    here
                  </p>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Recent Transactions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="glassmorphism rounded-xl p-6"
        >
          <h3 className="text-lg font-headline-bold text-foreground mb-6">
            Recent Credit Transactions
          </h3>
          {transactionHistory.length > 0 ? (
            <div className="space-y-3">
              {transactionHistory.slice(0, 5).map((transaction) => (
                <div
                  key={transaction.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-card/30 border border-border/50"
                >
                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${transaction.transaction_type === "purchase"
                          ? "bg-success/20"
                          : transaction.transaction_type === "deduction"
                            ? "bg-warning/20"
                            : "bg-error/20"
                        }`}
                    >
                      <Icon
                        name={
                          transaction.transaction_type === "purchase"
                            ? "Plus"
                            : transaction.transaction_type === "deduction"
                              ? "Minus"
                              : "RefreshCw"
                        }
                        size={16}
                        color={
                          transaction.transaction_type === "purchase"
                            ? "var(--color-success)"
                            : transaction.transaction_type === "deduction"
                              ? "var(--color-warning)"
                              : "var(--color-error)"
                        }
                      />
                    </div>
                    <div>
                      <p className="text-sm font-body-medium text-foreground">
                        {transaction.description ||
                          (transaction.transaction_type === "purchase"
                            ? "Credits Purchase"
                            : transaction.transaction_type === "deduction"
                              ? "Campaign Creation"
                              : "Credit Refund")}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(transaction.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p
                      className={`text-sm font-body-semibold ${transaction.transaction_type === "purchase"
                          ? "text-success"
                          : transaction.transaction_type === "deduction"
                            ? "text-warning"
                            : "text-error"
                        }`}
                    >
                      {transaction.transaction_type === "purchase" ? "+" : "-"}
                      {transaction.credits_amount} credits
                    </p>
                    {transaction.amount_paid && (
                      <p className="text-xs text-muted-foreground">
                        ${(transaction.amount_paid).toFixed(2)}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted/20 flex items-center justify-center">
                <Icon
                  name="Receipt"
                  size={32}
                  color="var(--color-muted-foreground)"
                />
              </div>
              <p className="text-muted-foreground font-body-medium">
                No transactions yet
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Your credit purchases and usage will appear here
              </p>
            </div>
          )}
        </motion.div>

        {/* Purchase Section (Replaces Modal) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="glassmorphism rounded-xl p-6"
        >
          <h3 className="text-lg font-headline-bold text-foreground mb-6">
            Buy More Credits
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {stripeService.getCreditPackages().map((pkg) => (
              <div
                key={pkg.id}
                className={`border rounded-xl p-6 glassmorphism shadow-md ${pkg.popular ? "border-primary/50" : "border-border/30"
                  }`}
              >
                {pkg.popular && (
                  <div className="text-xs font-semibold text-primary mb-2">
                    Most Popular
                  </div>
                )}
                <h4 className="text-lg font-headline-bold text-foreground mb-1">
                  {pkg.name}
                </h4>
                <p className="text-2xl font-headline-bold text-foreground mb-2">
                  {stripeService.formatPrice(pkg.price)}
                </p>
                <p className="text-sm text-muted-foreground mb-3">
                  {pkg.description}
                </p>
                <ul className="text-sm text-muted-foreground mb-4 space-y-1">
                  <li>• {pkg.campaigns}</li>
                  <li>• {pkg.credits} credits included</li>
                  {pkg.savings && <li>• Save {pkg.savings}</li>}
                </ul>
                <Button
                  onClick={() => handlePurchaseCredits(pkg)}
                  variant={pkg.popular ? "default" : "outline"}
                  className={pkg.popular ? "w-full cta-button" : "w-full"}
                >
                  Purchase Credits
                </Button>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
export default CreditsPage;
